import type {DiffUser} from '@github-ui/diff-lines/types'
import {parseDiffHash, parsePathDigestWithoutLineNumbers} from '@github-ui/diffs/document-hash-helpers'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import type {PullRequestState} from './PullRequestStateLabel'
import type {RepoSubject} from '@github-ui/comment-box/subject'
import {useSelectedFullDiffHash} from '@github-ui/diff-lines/use-selected-diff-hash'
import {SingleFileModePullRequestDiffEntry} from './SingleFileModePullRequestDiffEntry'
import {memo, useCallback, useEffect, useMemo, useRef} from 'react'
import {useDiffEntries, useDiffSummaries, useDiffSummaryCount, useUpdateSelectedPathDigest} from '../stores/selectors'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'

export interface SingleFileDiffEntryNavigatorProps {
  basePath: string
  commentBoxSubject?: RepoSubject
  contextLinesURL: string
  currentUser: DiffUser
  filteredDiffSummaries: PullRequestFileTreeDiff[]
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  pullRequestGlobalRelayId: string
  pullRequestState: PullRequestState
  repository: {
    id: number
    name: string
    ownerLogin: string
  }
}
export const SingleFileModeDiffEntryNavigator = memo(function SingleFileModeDiffEntryNavigator({
  basePath,
  commentBoxSubject,
  contextLinesURL,
  currentUser,
  filteredDiffSummaries,
  headBranchName,
  headRepoName,
  headRepoOwner,
  pullRequestGlobalRelayId,
  pullRequestState,
  repository,
}: SingleFileDiffEntryNavigatorProps) {
  const diffEntriesMap = useDiffEntries()
  const diffEntries = useMemo(() => {
    {
      return Object.values(diffEntriesMap ?? {}).filter(diffEntry => diffEntry !== null)
    }
  }, [diffEntriesMap])
  const diffEntry = diffEntries[0] // Get the first diff entry
  const selectedFullDiffHash = useSelectedFullDiffHash()
  const selectedPathDigest = parsePathDigestWithoutLineNumbers(selectedFullDiffHash) ?? undefined
  const diffSummariesCount = useDiffSummaryCount()
  const firstDiffSummary = useDiffSummaries()[0]

  const updateSelectedPathDigest = useUpdateSelectedPathDigest()
  const isFirstRender = useRef(true)
  const handleHashNavigation = useCallback((): void => {
    const existingHash = ssrSafeLocation.hash ?? ''
    let windowHash = parseDiffHash(existingHash) ?? ''
    windowHash = windowHash.replace('diff-', '').replace('#', '')

    const pathHash = selectedPathDigest || windowHash

    if (!pathHash) {
      // No hash, use the default entry
      return
    }
    if (!diffEntry) {
      // If no diff entry is available, use the first diff summary's path digest
      updateSelectedPathDigest(firstDiffSummary?.pathDigest)
      return
    }
    // ensure that the diffEntry doesn't match the url hash only on first visible render
    if (pathHash && (diffEntry.pathDigest !== pathHash || !isFirstRender.current)) {
      try {
        updateSelectedPathDigest(pathHash)
      } catch {
        // If the pathDigest is not found, we can ignore it
      }
    }
    isFirstRender.current = false
  }, [selectedPathDigest, diffEntry, updateSelectedPathDigest, firstDiffSummary?.pathDigest])

  /**
   * Run hash navigation on hash changes
   */
  useEffect(() => {
    const handleHashChange = (): void => {
      handleHashNavigation()
    }

    // Handle initial hash on mount
    handleHashNavigation()

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [handleHashNavigation])

  /*
   * Empty state for PRs with no changed files.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (filteredDiffSummaries.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <SingleFileModePullRequestDiffEntry
      diffEntryPathDigest={diffEntry?.pathDigest || firstDiffSummary?.pathDigest || ''}
      basePath={basePath}
      commentBoxSubject={commentBoxSubject}
      commentingEnabled
      contextLinesURL={contextLinesURL}
      currentUser={currentUser}
      diffManuallyExpanded={false}
      filteredDiffSummaries={filteredDiffSummaries}
      headBranchName={headBranchName}
      headRepoName={headRepoName}
      headRepoOwner={headRepoOwner}
      pullRequestGlobalRelayId={pullRequestGlobalRelayId}
      pullRequestState={pullRequestState}
      repository={repository}
    />
  )
})

try{ SingleFileModeDiffEntryNavigator.displayName ||= 'SingleFileModeDiffEntryNavigator' } catch {}